import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { FolaLogo } from '../components/FolaLogo';
import { EyeIcon, EyeOffIcon, GoogleGlyph } from '../components/icons';
import { Alert, Button, Field, INPUT_CLASS, LABEL_CLASS } from '../components/ui';
import { ApiError, errorMessage, login } from '../lib/api';
import { FORGOT_PASSWORD_ENABLED } from '../lib/flags';
import { normalizePhone } from '../lib/format';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: string; notice?: string } | null;
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!agreed) {
      setError('Harap setujui Syarat & Ketentuan serta Kebijakan Privasi.');
      return;
    }
    setError(null);
    setSubmitting(true);
    // Login hanya dengan nomor HP (arahan product); nomor lokal (08…/8…) dinormalkan ke +62.
    // Backend juga menerima email, tetapi form ini sengaja tidak menawarkannya.
    const phone = normalizePhone(identifier.trim());
    try {
      // Password boleh kosong: akun impor praregis yang belum punya password dijawab PASSWORD_NOT_SET.
      await login(phone, password);
      void navigate(state?.from ?? '/home', { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'PASSWORD_NOT_SET' && FORGOT_PASSWORD_ENABLED) {
        void navigate('/forgot-password', { state: { phone, passwordNotSet: true } });
        return;
      }
      setError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    // Desktop (Figma "Desktop - Registration 1"): latar lavender, logo besar kiri, kartu masuk kanan.
    <div className="md:grid md:w-full md:flex-1 md:grid-cols-2 md:items-center md:gap-10 md:bg-[#f5f3fe] md:px-10 lg:px-20">
      <div className="hidden justify-center md:flex">
        <img src="/assets/logo-large.png" alt="" width={1629} height={801} className="w-[min(28rem,85%)]" />
      </div>
      <div className="mx-auto flex w-full max-w-sm flex-col px-6 pt-16 pb-8 md:my-10 md:max-w-[29rem] md:justify-self-end md:rounded-[2rem] md:bg-white md:px-12 md:py-20 md:shadow-card">
        <div className="flex justify-center md:hidden">
          <FolaLogo size="lg" />
        </div>
        <h1 className="mt-10 text-center text-xl font-semibold text-fola-ink md:mt-0 md:text-left md:text-2xl">
          Sign in to your account
        </h1>

        {state?.notice ? (
          <div className="mt-4">
            <Alert tone="success">{state.notice}</Alert>
          </div>
        ) : null}

        <form className="mt-8 space-y-4" onSubmit={(e) => void submit(e)}>
          <Field
            label="Nomor HP (WhatsApp)"
            type="tel"
            inputMode="tel"
            autoComplete="username"
            placeholder="Enter your WhatsApp number"
            value={identifier}
            // Hanya angka, plus "+" di awal (format +62); huruf/simbol lain dibuang saat diketik/ditempel.
            onChange={(e) => setIdentifier(e.target.value.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, ''))}
            required
          />

          <div className="space-y-1.5">
            <label htmlFor="login-password" className={LABEL_CLASS}>
              Password
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                className={`${INPUT_CLASS} border-slate-200 pr-12`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute top-1/2 right-3 -translate-y-1/2 p-1 text-slate-500 hover:text-fola-ink"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Sembunyikan password' : 'Lihat password'}
              >
                {showPassword ? <EyeIcon className="size-5" /> : <EyeOffIcon className="size-5" />}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            {FORGOT_PASSWORD_ENABLED ? (
              <Link to="/forgot-password" className="text-[13px] text-slate-600 hover:text-fola-indigo">
                Forgot password?
              </Link>
            ) : (
              <span aria-disabled="true" className="cursor-not-allowed text-[13px] text-slate-600 opacity-50">
                Forgot password?
              </span>
            )}
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 pt-1">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 size-4 shrink-0 cursor-pointer appearance-none rounded-full border border-slate-300 bg-white transition checked:border-[5px] checked:border-fola-indigo focus-visible:ring-2 focus-visible:ring-fola-indigo/30 focus-visible:outline-none"
            />
            <span className="text-xs leading-snug text-slate-600 select-none">
              I&apos;ve read and agreed to{' '}
              <span className="font-semibold text-fola-indigo">User Agreement</span>
              <br />
              and <span className="font-semibold text-fola-indigo">Privacy Policy</span>
            </span>
          </label>

          {error ? <Alert tone="error">{error}</Alert> : null}

          <Button type="submit" className="!mt-6 w-full" loading={submitting}>
            Sign in
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-slate-500">other way to sign in</p>
        {/* Masuk dengan Google belum punya flow (Figma maupun backend): nonaktif. */}
        <button
          type="button"
          disabled
          className="mx-auto mt-3 flex size-11 cursor-not-allowed items-center justify-center rounded-full border border-slate-200 bg-white opacity-50"
          aria-label="Sign in with Google (belum tersedia)"
        >
          <GoogleGlyph className="size-5" />
        </button>

        <p className="mt-10 text-center text-xs text-slate-500">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="font-semibold text-fola-indigo hover:underline">
            Create Account
          </Link>
        </p>
      </div>
    </div>
  );
}
